"use client"

import { createContext, useEffect, useState, ReactNode } from 'react'

export const themes = {
  'purple-pink': {
    name: 'Purple Pink',
    primary: '147 51 234',
    primaryHover: '126 34 206',
    secondary: '219 39 119',
    secondaryHover: '190 24 93',
    accent: '236 72 153',
  },
  'blue-ocean': {
    name: 'Blue Ocean',
    primary: '37 99 235',
    primaryHover: '29 78 216',
    secondary: '6 182 212',
    secondaryHover: '14 165 233',
    accent: '34 197 94',
  },
  'emerald-green': {
    name: 'Emerald Green',
    primary: '5 150 105',
    primaryHover: '4 120 87',
    secondary: '34 197 94',
    secondaryHover: '21 128 61',
    accent: '101 163 13',
  },
  'orange-sunset': {
    name: 'Orange Sunset',
    primary: '234 88 12',
    primaryHover: '194 65 14',
    secondary: '220 38 127',
    secondaryHover: '185 28 28',
    accent: '251 146 60',
  },
  'royal-purple': {
    name: 'Royal Purple',
    primary: '109 40 217',
    primaryHover: '91 33 182',
    secondary: '168 85 197',
    secondaryHover: '147 51 234',
    accent: '196 181 253',
  },
} as const

export type ThemeName = keyof typeof themes
export type Theme = typeof themes[ThemeName]

export interface ThemeContextType {
  currentTheme: ThemeName
  theme: Theme
  setTheme: (theme: ThemeName) => void
  availableThemes: typeof themes
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
  children: ReactNode
  defaultTheme?: ThemeName
}

export function ThemeProvider({ children, defaultTheme = 'purple-pink' }: ThemeProviderProps) {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>(defaultTheme)

  const updateCSSVariables = (theme: Theme, themeName: ThemeName) => {
    const root = document.documentElement
    
    // Update CSS custom properties
    root.style.setProperty('--theme-primary', theme.primary)
    root.style.setProperty('--theme-primary-hover', theme.primaryHover)
    root.style.setProperty('--theme-secondary', theme.secondary)
    root.style.setProperty('--theme-secondary-hover', theme.secondaryHover)
    root.style.setProperty('--theme-accent', theme.accent)
    
    // Update data attribute for theme-specific styles
    root.setAttribute('data-theme', themeName)
  }

  const setTheme = (themeName: ThemeName) => {
    setCurrentTheme(themeName)
    localStorage.setItem('app-theme', themeName)
    updateCSSVariables(themes[themeName], themeName)
  }

  useEffect(() => {
    const savedTheme = localStorage.getItem('app-theme') as ThemeName
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme)
      updateCSSVariables(themes[savedTheme], savedTheme)
    } else {
      updateCSSVariables(themes[defaultTheme], defaultTheme)
    }
  }, [defaultTheme])

  return (
    <ThemeContext.Provider 
      value={{ 
        currentTheme, 
        theme: themes[currentTheme], 
        setTheme, 
        availableThemes: themes 
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
